import { Component, Inject, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as braintree from 'braintree-web';
import { BraintreeService } from '../../data-access/braintree.service';
import { BtAchBankAccountDialogComponent } from '../../ui/bt-ach-bank-account-dialog/bt-ach-bank-account-dialog';
import { MatCardModule } from '@angular/material/card';
import { Router } from '@angular/router';
import { CartStateService } from '../../data-access/cart-state.service';
import { OrderSummaryComponent } from '../../ui/order-summary/order-summary.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaymentsService } from '../../data-access/payments.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { BtAchAccountsComponent } from '../../ui/bt-ach-accounts/bt-ach-accounts.component';
import { MatButtonModule } from '@angular/material/button';
import { UPDATE_USER } from '@equine-ui-nx/injection-tokens';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BtAccountAddedDialogComponent } from '../../ui/bt-account-added-dialog/bt-account-added-dialog.component';
import { BtMicroDepositVerificationComponent } from '../../ui/bt-micro-deposit-verification/bt-micro-deposit-verification.component';
import { IBtUsBankAccount } from '../../interfaces/braintree.interfaces';


@Component({
  selector: 'equine-ui-nx-pay-with-bt-ach',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    OrderSummaryComponent,
    MatProgressSpinnerModule,
    MatDialogModule,
    BtAchAccountsComponent
  ],
  providers: [
    MatSnackBar
  ],
  templateUrl: './pay-with-bt-ach.component.html',
  styleUrl: './pay-with-bt-ach.component.scss',
})
export class PayWithBtAchComponent implements OnInit, OnDestroy {

  private router = inject(Router);
  private dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  
  private paymentsService = inject(PaymentsService);
  private braintreeService = inject(BraintreeService);
  private cartState = inject(CartStateService);

  unsubscribe$ = new Subject<void>();

  private cartId!: string;
  orders = this.cartState.ordersForSum;
  subTotal = this.cartState.subtotal;
  totalItems = this.cartState.totalItems;
  donationOrder = this.cartState.donationOrder;
  donationOrderTotal = this.cartState.donationOrderTotal;
  total = this.cartState.total;
  coupon = this.cartState.coupon;

  loading = signal(true);
  cartCompleted = signal(false);
  cartError = signal(null);

  cartReference = this.paymentsService.getLoggedInUserId() || '';
  cartIntent = 'CAPTURE';

  paymentMethods: IBtUsBankAccount[] = [];
  private clientInstance!: braintree.Client;
  private bankInstance!: braintree.USBankAccount;
  private dataCollectorInstance!: braintree.DataCollector;
  clientLoaded = signal(false);

  paying = signal(false);

  constructor(
    @Inject(UPDATE_USER) private updateUserToken: BehaviorSubject<void>
  ) {}


  ngOnInit(): void {
    this.getAmount();
    this.getBraintreeTokenAndCustomerData();
  }

  getAmount() {
    this.loading.set(true);
    
    this.paymentsService.getCartSummary(undefined, this.cartReference)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (response) => {
          this.cartIntent = response?.cart?.intent || 'CAPTURE';
          this.cartId = response.cart.id;
          this.loading.set(false);
          if (response.status === 'COMPLETE') {
            this.cartCompleted.set(true);
          }
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'An error ocurred. Cannot load cart'
          this.cartError.set(errorMessage);
          this.loading.set(false);
        }
      });
  }

  getBraintreeTokenAndCustomerData() {
    this.braintreeService.getBraintreeTokenAndCustomerData(this.cartReference)
    .pipe(
      takeUntil(this.unsubscribe$)
    )
    .subscribe({
      next: (response) => {
        this.paymentMethods = response.btCustomer.usBankAccounts || [];

        braintree.client.create({authorization: response.clientToken})
          .then(clientInstance => {
            this.clientInstance = clientInstance;
            this.clientLoaded.set(true);
              
            this.setDataCollector();
            this.setUsBankAccountInstance();
        });
      }

    })
  }

  setDataCollector() {
    braintree.dataCollector.create({
      client: this.clientInstance
    }).then((dataCollectorInstance) => {
      this.dataCollectorInstance = dataCollectorInstance;
    })
    .catch((err) => {
      void 0;
    }); 
  }

  setUsBankAccountInstance() {
    braintree.usBankAccount.create({
      client: this.clientInstance
    })
      .then((usBankAccountInstance: braintree.USBankAccount) => {
        this.bankInstance = usBankAccountInstance;
      })
      .catch((error) => {
        throw error;
      });
  }

  addNewBankAccount() {
    const dialogRef = this.dialog.open(BtAchBankAccountDialogComponent, {
      disableClose: true
    });

    dialogRef.componentInstance.formSubmit.asObservable()
    .pipe(
      takeUntil(this.unsubscribe$)
    )
    .subscribe({
      next: (formData: {bankDetails: any, mandateText: string}) => {
        dialogRef.componentInstance.isLoading.set(true);
        dialogRef.componentInstance.error.set(null);

        this.bankInstance.tokenize({
          bankDetails: formData.bankDetails,
          mandateText: formData.mandateText,
          bankLogin: null
        }, (tokenizeErr, tokenizedPayload: {description: string, details: any, nonce: string, type: string}) => {
          if (tokenizeErr) {
            console.error('There was an error tokenizing the bank details.');
            throw tokenizeErr;
          }

          // VAULT
          this.braintreeService.vaultBankAccount(this.cartReference, tokenizedPayload.nonce)
            .pipe(
              takeUntil(this.unsubscribe$)
            )
            .subscribe({
              next: (result: {success: boolean, verified: boolean}) => {
                
                this._snackBar.open(`Success!`, 'Dismiss', {
                  duration: 3000,
                  panelClass: ['success-snackbar'],
                });

                this.dialog.open(BtAccountAddedDialogComponent, {
                  data: {verified: result.verified}
                });

                dialogRef.close();
                this.getBraintreeTokenAndCustomerData();
              },
              error: (error) => {
                debugger
                dialogRef.componentInstance.isLoading.set(false);
                dialogRef.componentInstance.error.set(error.error.message);
                dialogRef.componentInstance.authorizationControl.reset();
              }
            })
          
          console.log(tokenizedPayload);
        });

      }
    })
  }

  payWithToken(selected: {token: string}) {
    
    if (!this.dataCollectorInstance) {
      return;
    }

    this.paying.set(true);
    this.braintreeService.captureWithBTAch(this.cartId, selected.token, this.dataCollectorInstance.deviceData)
      .subscribe({
        next: (success) => {
          this.paymentsService.setCartAsEmptyAfterPayment();
          this.fetchUserIfNeeded(success);
          this._snackBar.open(`Success!`, 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar'],
          });
          this.router.navigate(['payments', 'success']);
        },
        error: (error) => {
          this.paying.set(false);

          if (error?.error?.message) {
            this._snackBar.open(`Error: ${error.error.message}`, 'Dismiss')
          } else {
            this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
          }
        }
      })
  }

  removePaymentMethod(selected: {token: string}) {
    this.braintreeService.removeBtPaymentMethod(selected.token)
      .subscribe({
        next: (response) => {
          this.getBraintreeTokenAndCustomerData();
  
          this._snackBar.open(`Success!`, 'Dismiss', {
            duration: 3000,
            panelClass: ['success-snackbar'],
          });
        },
        error: (error) => {
          this._snackBar.open(`Error: ${error.error.message}`, 'Dismiss')
        }
      });
  }

  openVerificationDialog(account: IBtUsBankAccount) {
    const verificationDialogRef = this.dialog.open(BtMicroDepositVerificationComponent, {
      disableClose: true,
      data: {
        bankName: account.bankName,
        last4: account.last4
      }
    });

    verificationDialogRef.componentInstance.submitMicroDeposit
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (microdeposits: number[]) => {
          verificationDialogRef.componentInstance.loading = true;
          verificationDialogRef.componentInstance.form.disable();

          this.braintreeService.verifyAccount(account.verifications[0].id, microdeposits)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: (response) => {
                verificationDialogRef.close();
                this.dialog.open(BtAccountAddedDialogComponent, {
                  data: {verified: true}
                });
                this.getBraintreeTokenAndCustomerData();
              },
              error: (errorResponse) => {
                this.dialog.open(BtAccountAddedDialogComponent, {
                  data: {
                    verified: false,
                    message: 2,
                    messageError: errorResponse.error.message
                  }
                });

                if (errorResponse.error?.message === 'Too many confirmation attempts.') {
                  verificationDialogRef.close();
                } else {
                  verificationDialogRef.componentInstance.form.reset();
                  verificationDialogRef.componentInstance.form.enable();
  
                  verificationDialogRef.componentInstance.loading = false;
                }
              }
            });
        }
      });

  }

  fetchUserIfNeeded(successResponse: any) {
    this.updateUserToken.next();
  }

  goBack() {
    this.router.navigate(['payments', 'payment-summary'])
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
